import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Link2, FlaskConical } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { usePaymentLinkServices } from '@/hooks/use-payment-link';
import { useMerchantMap } from '@/hooks/use-merchant-map';
import { useAuth } from '@/hooks/use-auth';
import { formatDate } from '@/lib/utils';
import { ROLE_GROUPS } from '@/types/auth';
import type { PaymentLinkMerchantRow, PaymentLinkServiceConfig } from '@/types/payment-link';
import { MerchantStatusDrawer } from './merchant-status-drawer';
import { ActivateMerchantLauncher } from './activate-merchant-launcher';

/**
 * Payment-Link Verwaltung — Merchant-Uebersicht (Spec 493, Inc 1, read-only, platform-only).
 *
 * Die Provisioning-API hat keine "liste alle Merchants"-Route (D2/OQ-8); die Uebersicht wird
 * deshalb clientseitig aus dem Services-Aggregat nach MerchantId gruppiert. Bekannte Grenzen:
 * ein provisionierter Merchant mit 0 Services erscheint nicht (in Inc 1 folgenlos — der Drawer
 * ist nur ueber eine gelistete Zeile erreichbar); Namen ueber `resolveMerchantName` mit
 * Fallback auf die rohe merchantId (>200-Merchant-Cap von use-merchant-map).
 *
 * Keine Mutationen in Inc 1 — nur Lesen. platform_ops sieht dieselbe Flaeche wie platform_admin.
 */

function aggregateByMerchant(services: PaymentLinkServiceConfig[]): PaymentLinkMerchantRow[] {
  const byMerchant = new Map<string, PaymentLinkMerchantRow>();
  for (const svc of services) {
    const row = byMerchant.get(svc.MerchantId) ?? {
      merchantId: svc.MerchantId,
      serviceCount: 0,
      enabledServiceCount: 0,
      lastUpdatedAt: undefined,
    };
    row.serviceCount += 1;
    if (svc.Enabled) row.enabledServiceCount += 1;
    if (svc.UpdatedAt && (!row.lastUpdatedAt || svc.UpdatedAt > row.lastUpdatedAt)) {
      row.lastUpdatedAt = svc.UpdatedAt;
    }
    byMerchant.set(svc.MerchantId, row);
  }
  return Array.from(byMerchant.values());
}

export function PaymentLinkPage() {
  const { t } = useTranslation('payment-link');
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canWrite = hasRole(ROLE_GROUPS.PAYMENT_LINK_WRITE);
  const { data, isLoading, error, refetch } = usePaymentLinkServices();
  const { resolveMerchantName } = useMerchantMap();
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  // OQ-8: merchants activated this session that have 0 services do NOT appear in the aggregate
  // (D2). We merge a synthetic row so they stay visible until the aggregate catches up (or until
  // a hard reload — a full fix needs the upstream GET /provisioning/merchants list, OQ-8).
  const [activatedIds, setActivatedIds] = useState<string[]>([]);

  const handleActivated = (merchantId: string) => {
    setActivatedIds((prev) => (prev.includes(merchantId) ? prev : [...prev, merchantId]));
    setSelectedMerchantId(merchantId); // open the drawer directly for immediate confirmation
  };

  // Header-Aktionen: Link-Tester (Spec 501) fuer alle Leser der Flaeche + der
  // Aktivieren-Launcher nur fuer Schreibberechtigte.
  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate('/payment-link/tester')}
      >
        <FlaskConical className="mr-1 h-4 w-4" aria-hidden="true" />
        {t('tester.openLink')}
      </Button>
      {canWrite && <ActivateMerchantLauncher onActivated={handleActivated} />}
    </div>
  );

  const rows = useMemo(() => {
    const aggregated = aggregateByMerchant(data?.Services ?? []);
    const known = new Set(aggregated.map((r) => r.merchantId));
    // Session-merge: activated-but-0-service merchants that the aggregate does not yet show.
    for (const id of activatedIds) {
      if (!known.has(id)) {
        aggregated.push({ merchantId: id, serviceCount: 0, enabledServiceCount: 0 });
      }
    }
    return aggregated.sort((a, b) =>
      resolveMerchantName(a.merchantId).localeCompare(resolveMerchantName(b.merchantId)),
    );
  }, [data, resolveMerchantName, activatedIds]);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t('title')}
          description={t('description')}
          actions={headerActions}
        />
        <Card>
          <CardContent className="p-4">
            <TableSkeleton rows={5} cols={3} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title={t('title')}
          description={t('description')}
          actions={headerActions}
        />
        <ApiErrorDisplay error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={headerActions}
      />

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            <Link2 className="mx-auto mb-3 h-6 w-6" aria-hidden="true" />
            {t('overview.empty')}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0 py-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('overview.columns.merchant')}</TableHead>
                  <TableHead className="text-right">{t('overview.columns.services')}</TableHead>
                  <TableHead>{t('overview.columns.lastUpdated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.merchantId}
                    className="cursor-pointer"
                    onClick={() => setSelectedMerchantId(row.merchantId)}
                    tabIndex={0}
                    role="button"
                    aria-label={t('overview.openStatus', {
                      merchant: resolveMerchantName(row.merchantId),
                    })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedMerchantId(row.merchantId);
                      }
                    }}
                  >
                    <TableCell className="font-medium">
                      {resolveMerchantName(row.merchantId)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="outline">
                        {t('overview.serviceCount', {
                          enabled: row.enabledServiceCount,
                          total: row.serviceCount,
                        })}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.lastUpdatedAt ? formatDate(row.lastUpdatedAt) : t('common.notSet')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <MerchantStatusDrawer
        merchantId={selectedMerchantId}
        merchantName={
          selectedMerchantId ? resolveMerchantName(selectedMerchantId) : undefined
        }
        onOpenChange={(open) => {
          if (!open) setSelectedMerchantId(null);
        }}
      />
    </div>
  );
}
